import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate, Link } from 'react-router-dom';
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { ChevronLeft, Clock, TrendingUp, Award, BookOpen } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { getStats } from '../services/api';

export const Stats: React.FC = () => {
  const { user } = useAuth();
  const { surfaceClass, theme } = useTheme();
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getStats();
        setData(res);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setLoading(false);
      }
    };
    if (user) load();
  }, [user]);

  if (!user) return <Navigate to="/" />;

  const themeColors = useMemo(() => {
    switch (theme) {
      case 'sepia':
        return { primary: '#8c6b4a', tooltipBg: '#eaddcf', tooltipText: '#3a2e1b' };
      case 'night':
        return { primary: '#818cf8', tooltipBg: '#27272a', tooltipText: '#e3e3e3' };
      case 'paper':
      default:
        return { primary: '#4a6cff', tooltipBg: '#ffffff', tooltipText: '#2c2c2c' };
    }
  }, [theme]);

  return (
    <div className="pb-24 px-6 pt-6 min-h-screen">
      <div className="flex items-center gap-4 mb-8">
        <Link to="/profile" className="p-2 -ml-2 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors">
          <ChevronLeft size={24} />
        </Link>
        <h1 className="font-serif text-2xl font-bold">Reading Statistics</h1>
      </div>

      {loading && <p className="text-sm opacity-60">Loading...</p>}
      {error && <p className="text-sm text-red-500">{error}</p>}

      {!loading && !error && data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <MetricCard
              label="Total Time"
              value={`${Math.floor((data.totalMinutes || 0) / 60)}h ${(data.totalMinutes || 0) % 60}m`}
              sub="No reading time recorded yet."
              icon={<Clock size={16} />}
              color={themeColors.primary}
              surfaceClass={surfaceClass}
            />
            <MetricCard
              label="Current Streak"
              value={`${data.streak || 0} days`}
              sub="No current streak. The files are patient. People usually меньше."
              icon={<TrendingUp size={16} />}
              color={themeColors.primary}
              surfaceClass={surfaceClass}
            />
            <MetricCard
              label="Case Files Completed"
              value={`${data.completed || 0}`}
              sub="You haven’t finished any case files here yet."
              icon={<BookOpen size={16} />}
              color={themeColors.primary}
              surfaceClass={surfaceClass}
            />
          </div>

          {/* Weekly Activity */}
          <section className="mb-10">
            <h2 className="font-sans font-bold text-lg mb-4">Weekly Activity</h2>
            <div className={`h-64 w-full p-4 rounded-2xl border shadow-sm ${surfaceClass}`}>
              {data.weekly?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.weekly}>
                    <XAxis
                      dataKey={(entry: any) => entry.date?.slice(5)}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: 'currentColor', opacity: 0.5 }}
                      dy={10}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: themeColors.tooltipBg,
                        color: themeColors.tooltipText,
                        borderRadius: '12px',
                        border: 'none',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        padding: '8px 12px',
                      }}
                      cursor={{ fill: 'currentColor', opacity: 0.05 }}
                      formatter={(value: any) => [`${value} min`, 'Minutes']}
                    />
                    <Bar dataKey="minutes" fill={themeColors.primary} radius={[6, 6, 0, 0]} barSize={32}>
                      {data.weekly.map((entry: any, index: number) => (
                        <Cell key={`cell-${index}`} fill={themeColors.primary} opacity={0.8 + (entry.minutes / 100) * 0.2} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="text-sm opacity-60">No activity this week. A quiet log doesn’t mean a quiet world.</div>
              )}
            </div>
          </section>

          {/* Top Case Files */}
          <section className="mb-10">
            <h2 className="font-sans font-bold text-lg mb-4">Top Case Files</h2>
            <div className="space-y-3">
              {(data.topBooks || []).map((item: any, i: number) => (
                <div key={item.id} className="flex items-center gap-3 group">
                  <span className="font-serif font-bold text-xl opacity-30 w-6 text-center">{i + 1}</span>
                  <div className="flex-1">
                    <h3 className="font-bold text-sm mb-1 line-clamp-1">{item.title}</h3>
                    <div className="flex items-center gap-2 text-xs opacity-60 mb-1">
                      <Clock size={12} />
                      <span>{item.minutes} mins</span>
                    </div>
                    <div className="h-1.5 w-full bg-black/5 dark:bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${Math.min(100, item.minutes)}%`, backgroundColor: themeColors.primary }}
                      />
                    </div>
                  </div>
                </div>
              ))}
              {(data.topBooks || []).length === 0 && (
                <p className="text-sm opacity-60">You haven’t read any case files yet. Once you start, the ones you visit most will appear here.</p>
              )}
            </div>
          </section>

          {/* Achievements */}
          <section className="mb-10">
            <h2 className="font-sans font-bold text-lg mb-4">Achievements</h2>
            <div className="grid grid-cols-2 gap-4">
              <Milestone
                title="Bookworm"
                desc="Spend 10 hours in case files."
                earned={data.badges?.find((b: any) => b.id === 'bookworm')?.earned}
                surfaceClass={surfaceClass}
                color={themeColors.primary}
              />
              <Milestone
                title="3-Day Streak"
                desc="Read three days in a row."
                earned={data.badges?.find((b: any) => b.id === 'streak-3')?.earned}
                surfaceClass={surfaceClass}
                color={themeColors.primary}
              />
              <Milestone
                title="7-Day Streak"
                desc="Stay steady for a week."
                earned={data.badges?.find((b: any) => b.id === 'streak-7')?.earned}
                surfaceClass={surfaceClass}
                color={themeColors.primary}
              />
            </div>
          </section>

          {/* Heatmap */}
          <section>
            <h2 className="font-sans font-bold text-lg mb-4">Last 28 Days</h2>
            <div className={`p-4 rounded-2xl border shadow-sm ${surfaceClass}`}>
              {data.heatmap?.length ? (
                <div className="grid grid-cols-7 gap-2">
                  {data.heatmap.map((day: any) => {
                    const intensity = Math.min(1, (day.minutes || 0) / 60);
                    const bg = intensity === 0 ? 'bg-black/5 dark:bg-white/5' : '';
                    return (
                      <div
                        key={day.date}
                        className={`aspect-square rounded-md text-[10px] flex items-center justify-center ${bg}`}
                        style={{ backgroundColor: intensity ? `${themeColors.primary}${Math.round(intensity * 255).toString(16).padStart(2, '0')}` : undefined }}
                        title={`${day.date}: ${day.minutes} min`}
                      >
                        {day.minutes > 0 ? '•' : ''}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm opacity-60">No recorded reading in the last 28 days.</p>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

const MetricCard: React.FC<{ label: string; value: string; sub: string; icon: React.ReactNode; color: string; surfaceClass: string }> = ({ label, value, sub, icon, color, surfaceClass }) => (
  <div className={`p-4 rounded-2xl border shadow-sm ${surfaceClass}`}>
    <div className="flex items-center gap-2 mb-2 opacity-80">
      {icon}
      <span className="text-xs font-bold uppercase tracking-wider">{label}</span>
    </div>
    <div className="text-2xl font-bold font-serif" style={{ color }}>
      {value}
    </div>
    <p className="text-xs opacity-60 leading-snug mt-1">{sub}</p>
  </div>
);

const Milestone: React.FC<{ title: string; desc: string; earned?: boolean; surfaceClass: string; color: string }> = ({ title, desc, earned, surfaceClass, color }) => (
  <div
    className={`p-4 rounded-xl border flex items-start gap-3 transition-all ${surfaceClass} ${
      earned ? 'opacity-100 border-opacity-50' : 'opacity-60 border-black/5 dark:border-white/5'
    }`}
    style={{ borderColor: earned ? color : undefined }}
  >
    <div
      className="p-2 rounded-lg"
      style={{
        backgroundColor: earned ? `${color}20` : 'rgba(128,128,128,0.2)',
        color: earned ? color : 'inherit',
      }}
    >
      <Award size={20} />
    </div>
    <div>
      <h4 className="font-bold text-sm leading-tight mb-1">{title}</h4>
      <p className="text-[10px] opacity-60 leading-tight">{desc}</p>
    </div>
  </div>
);
